import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useBookings } from '../lib/bookingsContext';
import { SwitchPropertyDialog } from './SwitchPropertyDialog';

type Tab = 'rack' | 'tracker' | 'stays' | 'housekeeping' | 'keys' | 'availability';

interface ChromeProps {
  titleText: string;
  apiOnline: boolean;
  apiStatusLabel: string;
  userLabel: string;
  propertyLabel: string;
  lastRefresh: string;
  onRefresh: () => void;
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
  children: ReactNode;
  onProfileSearch?: () => void;
  onLogout?: () => void;
}


/**
 * Overdue count on the Room tracker tab.
 *
 * Its own component so that the one-second booking tick re-renders this badge
 * alone rather than the whole chrome.
 */
function TrackerBadge() {
  const { expiredCount } = useBookings();
  if (expiredCount === 0) return null;
  return <span className="tab-badge" title={`${expiredCount} overdue`}>{expiredCount}</span>;
}

function FileMenu({ onSwitchProperty, onLogout }: { onSwitchProperty: () => void; onLogout?: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const canSwitch = (user?.properties?.length ?? 0) > 1;

  return (
    <span className="menubar-item" ref={ref}>
      <button
        type="button"
        className={`menubar-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <u>F</u>ile
      </button>
      {open && (
        <div className="menubar-dropdown">
          <button
            type="button"
            className="menubar-menuitem"
            onClick={() => { setOpen(false); onSwitchProperty(); }}
            disabled={!canSwitch}
            title={canSwitch ? '' : 'You have access to only one property.'}
          >
            Switch Property…
            <span className="menubar-shortcut">Ctrl+Shift+P</span>
          </button>
          <div className="menubar-separator" />
          <button
            type="button"
            className="menubar-menuitem"
            onClick={() => { setOpen(false); onLogout?.(); }}
          >
            Sign out
          </button>
        </div>
      )}
    </span>
  );
}

function UserMenu({ label, onLogout }: { label: string; onLogout?: () => void }) {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [open]);
  return (
    <span className="user-menu" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen(v => !v)}
        title="Account menu"
      >
        {label} ▾
      </button>
      {open && (
        <div className="user-menu-popover">
          <button type="button" className="user-menu-item" onClick={() => { setOpen(false); onLogout?.(); }}>
            Sign out
          </button>
        </div>
      )}
    </span>
  );
}

export function Chrome({
  titleText, apiOnline, apiStatusLabel,
  userLabel, propertyLabel, lastRefresh,
  onRefresh, activeTab, onTabChange, children,
  onProfileSearch, onLogout,
}: ChromeProps) {
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const { user } = useAuth();

  // Ctrl+Shift+P opens Switch Property (skip for single-property users)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        if ((user?.properties?.length ?? 0) > 1) {
          e.preventDefault();
          setShowSwitchDialog(true);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [user]);

  return (
    <div className="window">
      <div className="titlebar">
        <div className="left"><span>🏢</span><span>{titleText}</span></div>
        <div className="right">
          <span className="winbtn">_</span>
          <span className="winbtn">□</span>
          <span className="winbtn close">×</span>
        </div>
      </div>

      <div className="menubar">
        <FileMenu onSwitchProperty={() => setShowSwitchDialog(true)} onLogout={onLogout} />
        <span><u>V</u>iew</span>
        <span><u>R</u>eservations</span>
        <span>Front <u>D</u>esk</span>
        <span>Rooms <u>M</u>gmt</span>
        <span><u>P</u>rofiles</span>
        <span>Re<u>p</u>orts</span>
        <span><u>H</u>elp</span>
      </div>

      <div className="toolbar">
        <button className="toolbtn" disabled>➕ New res</button>
        <button className="toolbtn" disabled>🚪 Check in</button>
        <button className="toolbtn" disabled>🚶 Check out</button>
        <button className="toolbtn" disabled>🔒 Block</button>
        <button className="toolbtn" disabled>🧹 Housekeeping</button>
        <button className="toolbtn" onClick={onProfileSearch}>👤 Profile</button>
        <button className="toolbtn" onClick={onRefresh}>🔄 Refresh</button>
      </div>

      <div className="tabs">
        <div
          className={`tab ${activeTab === 'rack' ? 'active' : ''}`}
          onClick={() => onTabChange('rack')}>Room plan</div>
        <div
          className={`tab ${activeTab === 'tracker' ? 'active' : ''}`}
          onClick={() => onTabChange('tracker')}>
          Room tracker
          <TrackerBadge />
        </div>
        <div
          className={`tab ${activeTab === 'stays' ? 'active' : ''}`}
          onClick={() => onTabChange('stays')}>Accommodation</div>
        <div className="tab">Front desk</div>
        <div className="tab">Housekeeping</div>
        <div className="tab">Profiles</div>
        <div
          className={`tab ${activeTab === 'keys' ? 'active' : ''}`}
          onClick={() => onTabChange('keys')}>Keys</div>
        <div
          className={`tab ${activeTab === 'availability' ? 'active' : ''}`}
          onClick={() => onTabChange('availability')}>Availability</div>
        <div className="tab">Reports</div>
      </div>

      <div className="body">
        <div className="rail">
          <button className="railbtn"><span className="icon">📝</span>New res</button>
          <button className="railbtn"><span className="icon">🚶</span>Walk-in</button>
          <button className="railbtn" onClick={onProfileSearch}><span className="icon">👥</span>Profiles</button>
          <button
            className={`railbtn ${activeTab === 'rack' ? 'active' : ''}`}
            onClick={() => onTabChange('rack')}>
            <span className="icon">🏠</span>Room plan
          </button>
          <button
            className={`railbtn ${activeTab === 'stays' ? 'active' : ''}`}
            onClick={() => onTabChange('stays')}>
            <span className="icon">📋</span>Res list
          </button>
          <button className="railbtn"><span className="icon">🔒</span>Block</button>
          <button className={`railbtn ${activeTab === 'keys' ? 'active' : ''}`} onClick={() => onTabChange('keys')}><span className="icon">🔑</span>Keys</button>
          <button className={`railbtn ${activeTab === 'housekeeping' ? 'active' : ''}`} onClick={() => onTabChange('housekeeping')}><span className="icon">🧹</span>Hskpg</button>
          <button className={`railbtn ${activeTab === 'availability' ? 'active' : ''}`} onClick={() => onTabChange('availability')}><span className="icon">📅</span>Avail</button>
        </div>
        <div className="content">{children}</div>
      </div>

      <div className="statusbar">
        <div className="group">
          <span>
            <span className={apiOnline ? 'dot' : 'dot offline'} />
            {apiStatusLabel}
          </span>
          <span><UserMenu label={userLabel} onLogout={onLogout} /></span>
          <span>{propertyLabel}</span>
        </div>
        <div className="group">
          <span>{lastRefresh}</span>
          <span>v0.1.0</span>
        </div>
      </div>
      {showSwitchDialog && <SwitchPropertyDialog onClose={() => setShowSwitchDialog(false)} />}
    </div>
  );
}
